import { useState, useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, FilePlus2, Upload, Paperclip, Link2, Plus, Building2 } from 'lucide-react'
import {
  createTask,
  createAssignment,
  updateAssignment,
  deleteAssignment,
  createTaskLink,
  uploadTaskAttachment,
  type Assignment,
  type Client,
  type Employee,
} from '../../lib/api'
import { useDisciplines } from '../../hooks/useDisciplines'
import { pastedFileName } from '../../hooks/useTaskAttachments'
import { handoffStep } from '../assignment/handoff'
import {
  emptyDraft,
  checkDraft,
  partitionFiles,
  followUpNotice,
  followUpTab,
  type NewTaskDraft,
  type DraftField,
  type FollowUpFailure,
} from './newTask'
import type { TaskDetailTab } from './TaskDetail'

interface NewTaskModalProps {
  onClose: () => void
  /**
   * Called once the task exists. The caller opens its card — on `tab` with
   * `notice` when some of what came with it (people, links, files) didn't save.
   */
  onCreated: (taskId: string, opts: { tab: TaskDetailTab; notice: string | null }) => void
  employees?: Employee[]
  clients?: Client[]
  /** Shows the people pickers. `roleCanWrite`. */
  canAssign?: boolean
  /** Locks the client — the form was opened from that client's page. */
  lockedClient?: { id: string; name: string } | null
  /** Carried over from Quick Add's "More details". */
  initialTitle?: string
  /** Signed-in employee, so handing the Lead to someone keeps them on as Contributor. */
  currentEmployeeId?: string | null
  /**
   * The signed-in role is assignment-scoped, so `POST /api/tasks` makes them the
   * Lead automatically — an empty Lead picker means "me", not "unassigned".
   */
  creatorIsLead?: boolean
}

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'med', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
]

/**
 * The full creation form (LXP-45). Sits alongside Quick Add — which stays the
 * fast path — for tasks that should be complete the moment they exist.
 */
export function NewTaskModal({
  onClose,
  onCreated,
  employees = [],
  clients = [],
  canAssign = true,
  lockedClient = null,
  initialTitle = '',
  currentEmployeeId = null,
  creatorIsLead = false,
}: NewTaskModalProps) {
  const qc = useQueryClient()
  const disciplines = useDisciplines()
  const titleRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [draft, setDraft] = useState<NewTaskDraft>(() =>
    emptyDraft({ title: initialTitle, clientId: lockedClient?.id ?? '' }),
  )
  const [files, setFiles] = useState<File[]>([])
  const [fileError, setFileError] = useState<string | null>(null)
  const [errors, setErrors] = useState<Partial<Record<DraftField, string>>>({})
  const [dropping, setDropping] = useState(false)

  // Disciplines load asynchronously; default to the tenant's first once known.
  useEffect(() => {
    if (!draft.discipline && disciplines[0]) setDraft((d) => ({ ...d, discipline: disciplines[0]! }))
  }, [disciplines, draft.discipline])

  useEffect(() => {
    titleRef.current?.focus()
  }, [])

  const set = <K extends keyof NewTaskDraft>(key: K, value: NewTaskDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }))

  const dirty =
    draft.title.trim() !== initialTitle.trim() ||
    !!draft.description.trim() ||
    !!draft.start ||
    !!draft.due ||
    !!draft.estimate.trim() ||
    !!draft.leadId ||
    draft.contributorIds.length > 0 ||
    draft.links.some((l) => l.url.trim() || l.label.trim()) ||
    files.length > 0

  const requestClose = () => {
    if (mutation.isPending) return
    if (dirty && !confirm('Discard this task? Nothing has been saved yet.')) return
    onClose()
  }

  // Close on Escape — through the same dirty check, so a stray key can't wipe a long form.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  })

  const stageFiles = (incoming: File[], renameTo?: (f: File) => string) => {
    if (!incoming.length) return
    const named = renameTo
      ? incoming.map((f) => new File([f], renameTo(f), { type: f.type }))
      : incoming
    const { ok, tooBig } = partitionFiles(named)
    setFileError(
      tooBig.length
        ? `${tooBig.map((f) => f.name || 'file').join(', ')} exceeds the 10MB limit and was not added.`
        : null,
    )
    if (ok.length) setFiles((prev) => [...prev, ...ok])
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = Array.from(e.clipboardData?.files ?? [])
    if (!pasted.length) return // plain text pastes into the field as usual
    e.preventDefault()
    const stamp = new Date().toISOString()
    stageFiles(pasted, (f) => pastedFileName(f, stamp))
  }

  const mutation = useMutation({
    mutationFn: async (plan: Extract<ReturnType<typeof checkDraft>, { ok: true }>) => {
      // The create is the only step allowed to fail the whole form. Everything
      // after it needs the task's id and is collected, never thrown — a task
      // that exists must reach the card even if a file didn't.
      const task = (await createTask(plan.payload)) as { id: string; assignments?: Assignment[] }
      const failures: FollowUpFailure[] = []
      const nameOf = (id: string) => employees.find((e) => e.id === id)?.name ?? 'teammate'

      if (canAssign && plan.leadId) {
        // A scoped creator is already the lead (`POST /api/tasks` auto-assigns).
        // Handing the lead to someone else keeps them on as Contributor.
        const step = handoffStep(task.assignments ?? [], {
          currentEmployeeId,
          newEmployeeId: plan.leadId,
          newRole: 'lead',
        })
        try {
          await createAssignment({ employeeId: plan.leadId, entityType: 'task', entityId: task.id, role: 'lead' })
          if (step?.kind === 'demote') await updateAssignment(step.assignmentId, { role: 'contributor' })
          if (step?.kind === 'drop') await deleteAssignment(step.assignmentId)
        } catch {
          failures.push({ kind: 'assignee', name: nameOf(plan.leadId) })
        }
      }

      if (canAssign) {
        for (const id of plan.contributorIds) {
          try {
            await createAssignment({ employeeId: id, entityType: 'task', entityId: task.id, role: 'contributor' })
          } catch {
            failures.push({ kind: 'assignee', name: nameOf(id) })
          }
        }
      }

      for (const link of plan.links) {
        try {
          await createTaskLink(task.id, link)
        } catch {
          failures.push({ kind: 'link', name: link.label ?? new URL(link.url).hostname })
        }
      }

      for (const file of files) {
        try {
          await uploadTaskAttachment(task.id, file)
        } catch {
          failures.push({ kind: 'file', name: file.name })
        }
      }

      return { taskId: task.id, failures }
    },
    onSuccess: ({ taskId, failures }) => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['assignments'] })
      qc.invalidateQueries({ queryKey: ['task-attachments', taskId] })
      qc.invalidateQueries({ queryKey: ['task-links', taskId] })
      onCreated(taskId, { tab: followUpTab(failures), notice: followUpNotice(failures) })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const plan = checkDraft(draft)
    if (!plan.ok) {
      setErrors(plan.errors)
      if (plan.errors.title) titleRef.current?.focus()
      return
    }
    setErrors({})
    mutation.mutate(plan)
  }

  const activeEmployees = employees.filter((e) => e.active)
  const contributorOptions = activeEmployees.filter(
    (e) => e.id !== draft.leadId && !draft.contributorIds.includes(e.id),
  )

  return (
    <div className="quick-add-backdrop" onClick={requestClose}>
      <div
        className="intake-modal new-task"
        onClick={(e) => e.stopPropagation()}
        onPaste={handlePaste}
        role="dialog"
        aria-modal="true"
        aria-label="New task"
      >
        <div className="intake-header">
          <span className="intake-header-icon">
            <FilePlus2 size={18} />
          </span>
          <div className="intake-header-text">
            <h3>New task</h3>
            <p>Everything at once — details, people, links and files. Quick add is still there for a title-only task.</p>
          </div>
          <button onClick={requestClose} className="btn-icon intake-close" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <form className="new-task__body" onSubmit={handleSubmit} noValidate>
          <div className="intake-field">
            <label htmlFor="nt-title">Title</label>
            <input
              id="nt-title"
              ref={titleRef}
              className="intake-input"
              value={draft.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="What needs doing?"
              aria-invalid={!!errors.title}
            />
            {errors.title && <span className="new-task__error">{errors.title}</span>}
          </div>

          <div className="intake-field">
            <label htmlFor="nt-desc">
              Description <span className="intake-optional">optional</span>
            </label>
            <textarea
              id="nt-desc"
              className="intake-input intake-textarea new-task__description"
              value={draft.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Context, acceptance criteria, instructions…"
              rows={5}
            />
          </div>

          <div className="intake-grid-2">
            <div className="intake-field">
              <label htmlFor="nt-client">Client</label>
              {lockedClient ? (
                <span className="new-task__locked" id="nt-client">
                  <Building2 size={13} /> {lockedClient.name}
                </span>
              ) : (
                <select
                  id="nt-client"
                  className="intake-input"
                  value={draft.clientId}
                  onChange={(e) => set('clientId', e.target.value)}
                >
                  <option value="">No client</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="intake-grid-2">
              <div className="intake-field">
                <label htmlFor="nt-priority">Priority</label>
                <select
                  id="nt-priority"
                  className="intake-input"
                  value={draft.priority}
                  onChange={(e) => set('priority', e.target.value)}
                >
                  {PRIORITIES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="intake-field">
                <label htmlFor="nt-discipline">Discipline</label>
                <select
                  id="nt-discipline"
                  className="intake-input"
                  value={draft.discipline}
                  onChange={(e) => set('discipline', e.target.value)}
                >
                  {disciplines.map((d) => (
                    <option key={d} value={d}>
                      {d.charAt(0).toUpperCase() + d.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="intake-grid-2">
            <div className="intake-grid-2">
              <div className="intake-field">
                <label htmlFor="nt-start">Start</label>
                <input
                  id="nt-start"
                  type="date"
                  className="intake-input"
                  value={draft.start}
                  onChange={(e) => set('start', e.target.value)}
                />
              </div>
              <div className="intake-field">
                <label htmlFor="nt-due">Due</label>
                <input
                  id="nt-due"
                  type="date"
                  className="intake-input"
                  value={draft.due}
                  min={draft.start || undefined}
                  onChange={(e) => set('due', e.target.value)}
                  aria-invalid={!!errors.due}
                />
                {errors.due && <span className="new-task__error">{errors.due}</span>}
              </div>
            </div>
            <div className="intake-field">
              <label htmlFor="nt-estimate">
                Estimate <span className="intake-optional">hours</span>
              </label>
              <input
                id="nt-estimate"
                className="intake-input"
                inputMode="numeric"
                value={draft.estimate}
                onChange={(e) => set('estimate', e.target.value)}
                placeholder="e.g. 4"
                aria-invalid={!!errors.estimate}
              />
              {errors.estimate && <span className="new-task__error">{errors.estimate}</span>}
            </div>
          </div>

          {canAssign && activeEmployees.length > 0 && (
            <div className="intake-grid-2">
              <div className="intake-field">
                <label htmlFor="nt-lead">Lead</label>
                <select
                  id="nt-lead"
                  className="intake-input"
                  value={draft.leadId}
                  onChange={(e) => {
                    const leadId = e.target.value
                    setDraft((d) => ({
                      ...d,
                      leadId,
                      contributorIds: d.contributorIds.filter((id) => id !== leadId),
                    }))
                  }}
                >
                  <option value="">{creatorIsLead ? 'Me' : 'Unassigned'}</option>
                  {activeEmployees
                    .filter((e) => !creatorIsLead || e.id !== currentEmployeeId)
                    .map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.name}
                      </option>
                    ))}
                </select>
              </div>
              <div className="intake-field">
                <label htmlFor="nt-contrib">
                  Contributors <span className="intake-optional">optional</span>
                </label>
                <div className="new-task__chips">
                  {draft.contributorIds.map((id) => (
                    <span key={id} className="chip chip-sm new-task__chip">
                      {employees.find((e) => e.id === id)?.name ?? 'Teammate'}
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => set('contributorIds', draft.contributorIds.filter((c) => c !== id))}
                        aria-label="Remove contributor"
                      >
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                  {contributorOptions.length > 0 && (
                    <select
                      id="nt-contrib"
                      className="intake-input new-task__chip-picker"
                      value=""
                      onChange={(e) => {
                        if (e.target.value) set('contributorIds', [...draft.contributorIds, e.target.value])
                      }}
                    >
                      <option value="">Add…</option>
                      {contributorOptions.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="intake-field">
            <label>
              <Link2 size={12} /> Links <span className="intake-optional">optional</span>
            </label>
            {draft.links.map((link, i) => (
              <div key={i} className="new-task__link-row">
                <input
                  className="intake-input"
                  value={link.url}
                  onChange={(e) =>
                    set('links', draft.links.map((l, j) => (j === i ? { ...l, url: e.target.value } : l)))
                  }
                  placeholder="https://…"
                  aria-label="Link URL"
                />
                <input
                  className="intake-input"
                  value={link.label}
                  onChange={(e) =>
                    set('links', draft.links.map((l, j) => (j === i ? { ...l, label: e.target.value } : l)))
                  }
                  placeholder="Label (optional)"
                  aria-label="Link label"
                />
                <button
                  type="button"
                  className="btn-icon"
                  onClick={() => set('links', draft.links.filter((_, j) => j !== i))}
                  aria-label="Remove link"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-ghost btn-xs new-task__add-link"
              onClick={() => set('links', [...draft.links, { url: '', label: '' }])}
            >
              <Plus size={12} /> Add link
            </button>
            {errors.links && <span className="new-task__error">{errors.links}</span>}
          </div>

          <div className="intake-field">
            <label>
              <Paperclip size={12} /> Files <span className="intake-optional">optional · 10MB each</span>
            </label>
            <div
              className={`intake-dropzone new-task__dropzone${dropping ? ' intake-dropzone--active' : ''}`}
              onDragOver={(e) => {
                e.preventDefault()
                setDropping(true)
              }}
              onDragLeave={() => setDropping(false)}
              onDrop={(e) => {
                e.preventDefault()
                setDropping(false)
                stageFiles(Array.from(e.dataTransfer.files))
              }}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click()
              }}
            >
              <Upload size={18} className="intake-dropzone-icon" />
              <span className="intake-dropzone-sub">Drop files, paste a screenshot, or click to browse</span>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  stageFiles(Array.from(e.target.files ?? []))
                  e.target.value = ''
                }}
              />
            </div>
            {files.length > 0 && (
              <ul className="new-task__files">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`}>
                    <Paperclip size={12} />
                    <span className="new-task__file-name">{f.name}</span>
                    <span className="text-muted text-xs">{Math.max(1, Math.round(f.size / 1024))} KB</span>
                    <button
                      type="button"
                      className="btn-icon"
                      onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                      aria-label={`Remove ${f.name}`}
                    >
                      <X size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {fileError && <span className="new-task__error">{fileError}</span>}
          </div>

          {mutation.isError && (
            <p className="intake-errors intake-errors--block" role="alert">
              {mutation.error instanceof Error ? mutation.error.message : 'Could not create the task'}
            </p>
          )}

          <div className="intake-footer">
            <p className="intake-footer-note">
              {files.length > 0
                ? `${files.length} file${files.length === 1 ? '' : 's'} will upload once the task is created.`
                : 'The task opens when it’s created.'}
            </p>
            <div className="new-task__actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={requestClose} disabled={mutation.isPending}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={mutation.isPending}>
                {mutation.isPending ? 'Creating…' : 'Create task'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
